import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Download, Eye, Image as ImageIcon, Loader2, Presentation, Trash2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTIONS = ['Bu Sürümde Yenilikler', 'Sistem Hakkında ve Roller', 'Giriş, Hesap ve Güvenlik', 'Ana Menüler ve Kendi Menünüzü Oluşturma (Sürükle-Bırak, Favoriler)',
  'Çalışma İzni Süreci: Sihirbaz, Animasyonlu Rehberler, Tehlike Sınıfı, İSG İncelemesi, QR Onay', 'Mağazalar ve Dış Firma Taleplerinde Mağaza Onayı / İtiraz',
  'KVKK, Kimlik Maskeleme, Sağlık Raporu Maskeleme ve İSG Düzeltme Akışı, Taahhütnameler', 'Uzaktan Destek: Firma Erişimi ve Erişim Geçmişi', 'Mobil Uyum, PWA ve Çevrimdışı Mod', 'Lokasyona Göre Tarih / Saat Kısıtlamaları ve Özel Durumlar', 'Five Star Denetimi, Firma Değerlendirme, Uzman Performansı',
  'Duyurular, Dokümanlar, E-posta Bildirimleri ve Otomatik İşler', 'Yönetim: Kullanıcılar, Sistem Ayarları, Tanıtım Sayfası'];

const LogoCard = () => {
  const ref = useRef(null);
  const [v, setV] = useState(Date.now());
  const [has, setHas] = useState(true);
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try { await axios.post(`${API}/settings/guide-logo`, fd); setHas(true); setV(Date.now()); toast.success('Kılavuz logosu yüklendi'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Logo yüklenemedi'); }
  };
  const remove = async () => { await axios.delete(`${API}/settings/guide-logo`); setHas(false); toast.success('Logo kaldırıldı'); };
  return (
    <div className="flex flex-wrap items-center gap-5 rounded-xl border bg-white p-5" data-testid="guide-logo-card">
      <div className="flex h-24 w-56 items-center justify-center rounded-lg border-2 border-dashed bg-slate-900 p-2">
        {has ? <img src={`${API}/public/guide-logo?v=${v}`} alt="Kılavuz logosu" className="max-h-full max-w-full object-contain" onError={() => setHas(false)} data-testid="guide-logo-preview" />
          : <span className="flex flex-col items-center text-xs text-slate-400"><ImageIcon className="mb-1 h-6 w-6" />Logo yok</span>}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-800">Kılavuz Logosu</p>
        <p className="text-xs text-slate-500">Tüm kılavuzların (yönetici ve firma, PDF ve PowerPoint) kapağında ve sayfa başlığında, ayrıca tanıtım sayfasının üst kısmında kullanılır. Koyu zemin üzerinde görüneceği için açık renkli / şeffaf PNG önerilir.</p>
        <div className="mt-3 flex gap-2">
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="guide-logo-input" />
          <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid="guide-logo-upload"><Upload className="mr-1 h-4 w-4" />Logo Yükle</Button>
          {has && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="guide-logo-delete"><Trash2 className="mr-1 h-4 w-4" />Kaldır</Button>}
        </div>
      </div>
    </div>
  );
};

// Süper Admin: tüm özellikleri anlatan pazarlama / kullanım kılavuzu PDF'i
export default function UserGuidePage() {
  const [busy, setBusy] = useState(false);
  const get = async (download, fmt = 'pdf') => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/user-guide/${fmt}`, { responseType: 'blob' });
      const href = URL.createObjectURL(r.data);
      if (!download) window.open(href, '_blank');
      else { const a = document.createElement('a'); a.href = href; a.download = `AVM_Belge_Takip_Kullanim_Kilavuzu.${fmt}`; a.click(); }
    } catch { toast.error('Kılavuz oluşturulamadı'); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-4xl space-y-6" data-testid="user-guide-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><BookOpen className="h-6 w-6 text-orange-500" />Kullanım Kılavuzu</h1>
        <p className="text-sm text-slate-500">Sistemin tüm özelliklerini, kullanıcı rollerini ve iş akışlarını ekran görüntüleriyle anlatan, yatay sunum düzeninde kılavuz. Tanıtım ve eğitimde PDF veya düzenlenebilir PowerPoint olarak kullanılabilir.</p>
      </div>
      <div className="flex gap-3">
        <Button onClick={() => get(true)} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="user-guide-download">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}PDF İndir</Button>
        <Button variant="outline" onClick={() => get(true, 'pptx')} disabled={busy} data-testid="user-guide-pptx"><Presentation className="mr-2 h-4 w-4" />PowerPoint İndir</Button>
        <Button variant="outline" onClick={() => get(false)} disabled={busy} data-testid="user-guide-view"><Eye className="mr-2 h-4 w-4" />Görüntüle</Button>
      </div>
      <LogoCard />
      <div className="rounded-xl border bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-slate-800">İçindekiler</p>
        <ol className="space-y-1.5 text-sm text-slate-600" data-testid="user-guide-toc">
          {SECTIONS.map((s, i) => <li key={s} className="flex gap-2"><span className="font-mono font-bold text-orange-500">{String(i + 1).padStart(2, '0')}</span>{s}</li>)}
        </ol>
      </div>
    </div>
  );
}
